import React from "react";
import { Link } from "react-router-dom";
import {
  Home, Users, Heart, Phone, CheckCircle2, BookOpen, Mail, Briefcase, ArrowRight, MapPin,
} from "lucide-react";
import { useSEO, breadcrumbSchema } from "../lib/seo";

// Easy Read — short sentences, plain English, large readable type, icon support.
// Audited against Australian Easy Read style guidance (one idea per sentence,
// active voice, ≤ 15 words per sentence where possible).

const SECTIONS = [
  {
    id: "who",
    Icon: Users,
    title: "Who we are",
    points: [
      "Alpha Community Care helps people with disability.",
      "We are an NDIS provider in Australia.",
      "We have homes and support workers in Melbourne and Perth.",
      "We help people across all of Australia too.",
    ],
  },
  {
    id: "homes",
    Icon: Home,
    title: "Our homes",
    points: [
      "Our homes are safe and clean.",
      "Some homes are for one person.",
      "Some homes are for a small group of people.",
      "We show you photos of every home.",
      "You can visit before you decide.",
    ],
    cta: { to: "/vacancies", label: "See our homes" },
  },
  {
    id: "support",
    Icon: Heart,
    title: "Our support workers",
    points: [
      "Support workers help every day.",
      "They help with cooking and cleaning.",
      "They help with showers and getting dressed.",
      "They help you go out and have fun.",
      "They are kind and they listen to you.",
    ],
  },
  {
    id: "services",
    Icon: BookOpen,
    title: "What we do",
    points: [
      "SIL — help to live in your own home.",
      "SDA — homes built for people with high support needs.",
      "Daily living — help with everyday things.",
      "Community — help to go out and meet people.",
      "Nursing — health support at home.",
      "Coordination — help to use your NDIS plan.",
    ],
    cta: { to: "/services", label: "See our services" },
  },
  {
    id: "start",
    Icon: CheckCircle2,
    title: "How to start",
    points: [
      "Call us on 1300 380 692.",
      "Or fill in our easy form.",
      "We will call you back.",
      "We will talk about what you need.",
      "We answer in one work day.",
    ],
    cta: { to: "/referrals/participant", label: "Fill the easy form" },
  },
  {
    id: "talk",
    Icon: Phone,
    title: "Talk to us",
    points: [
      "Phone: 1300 380 692",
      "Email: hello@alphacommunitycare.com.au",
      "We work with families.",
      "We work with support coordinators.",
      "We listen first.",
    ],
    cta: { to: "/contact", label: "Contact us" },
  },
];

export default function EasyReadPage() {
  useSEO({
    title: "Easy Read · Alpha Community Care · simple words and pictures",
    description:
      "An Easy Read version of Alpha Community Care: simple words, big text, pictures. Learn about our NDIS homes and supports.",
    canonical: "https://alphacommunitycare.com.au/easy-read",
    jsonLd: [breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Easy Read", path: "/easy-read" },
    ])],
  });

  return (
    <div data-testid="page-easy-read">
      {/* HERO */}
      <section className="alpha-gradient-hero border-b border-[color:var(--alpha-line)] relative overflow-hidden">
        <div className="alpha-orb alpha-orb-purple w-[280px] h-[280px] -left-12 top-8 opacity-65 hidden md:block" aria-hidden />
        <div className="alpha-container pt-12 pb-12 md:pt-16 md:pb-16 relative">
          <div className="alpha-overline">Easy read</div>
          <h1 className="mt-3 text-[34px] sm:text-5xl md:text-6xl font-display font-semibold tracking-tight leading-[1.05]">
            Alpha Community Care — <span className="alpha-serif-italic alpha-gradient-text">simple words</span>.
          </h1>
          <p className="mt-5 max-w-2xl text-lg md:text-xl leading-relaxed">
            We help people with disability live well. We have homes. We have support workers. We are here when you are ready.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="tel:1300380692"
              className="alpha-btn-primary !py-4 !px-6 text-base"
              data-testid="er-cta-phone"
            >
              <Phone className="w-5 h-5" /> Call 1300 380 692
            </a>
            <Link
              to="/referrals/participant"
              className="alpha-btn-green !py-4 !px-6 text-base"
              data-testid="er-cta-form"
            >
              Easy form <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="mt-5 inline-flex items-center gap-2 text-sm text-[color:var(--alpha-mute)]">
            <MapPin className="w-4 h-4 text-[color:var(--alpha-purple-700)]" />
            We work in Melbourne, Perth, and across Australia.
          </div>
        </div>
      </section>

      {/* CARD SECTIONS */}
      <section className="alpha-section">
        <div className="alpha-container max-w-3xl space-y-7 text-lg leading-relaxed">
          {SECTIONS.map((s, i) => (
            <article
              key={s.id}
              className="alpha-card p-6 md:p-8 alpha-reveal-stagger"
              data-testid={`er-section-${s.id}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start gap-4">
                <span className="w-12 h-12 rounded-2xl alpha-gradient text-white flex items-center justify-center shrink-0 shadow-md" aria-hidden>
                  <s.Icon className="w-6 h-6" />
                </span>
                <div className="flex-1">
                  <h2 className="font-display text-2xl md:text-[26px] font-semibold leading-tight">{s.title}</h2>
                  <ul className="mt-4 space-y-3">
                    {s.points.map((pt, k) => (
                      <li key={k} className="flex items-start gap-2.5">
                        <span className="mt-2 w-2 h-2 rounded-full bg-[color:var(--alpha-green-500)] shrink-0" aria-hidden />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                  {s.cta && (
                    <Link
                      to={s.cta.to}
                      className="mt-5 inline-flex items-center gap-2 alpha-btn-primary text-base !py-3 !px-5"
                      data-testid={`er-cta-${s.id}`}
                    >
                      {s.cta.label} <ArrowRight className="w-5 h-5" />
                    </Link>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* FINAL CTA strip */}
      <section className="alpha-section-tight">
        <div className="alpha-container max-w-3xl">
          <div className="alpha-card p-7 md:p-8 text-center">
            <Briefcase className="w-10 h-10 text-[color:var(--alpha-purple-700)] mx-auto" aria-hidden />
            <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold leading-tight">
              We are ready when you are.
            </h2>
            <p className="mt-3 text-[color:var(--alpha-mute)] text-base md:text-lg leading-relaxed">
              Call us, write to us, or fill in the easy form. We answer in one work day.
            </p>
            <div className="mt-5 flex flex-wrap gap-3 justify-center">
              <a href="tel:1300380692" className="alpha-btn-primary !py-3 !px-5 text-base">
                <Phone className="w-5 h-5" /> Call us
              </a>
              <a href="mailto:hello@alphacommunitycare.com.au" className="alpha-btn-ghost !py-3 !px-5 text-base">
                <Mail className="w-5 h-5" /> Email us
              </a>
              <Link to="/" className="alpha-btn-ghost !py-3 !px-5 text-base">
                Go to main site <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
